import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountLog} from 'src/openapi/admin/apis/AccountLogApi';
import {useAppDispatch} from 'src/store/hooks';

import {accountAuditLogStateSelector} from '../slices/account-audit-log-state.slice';
import {
    accountAuditLogActions,
    accountAuditLogDataSelector,
    accountAuditLogResultSelector,
} from '../slices/account-audit-log.slice';
import {AccountAuditLogCriteria} from '../typedef';

import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {AccountLogsPage} from 'src/openapi/admin/models/AccountLogsPage';

type GetAccountAuditLogCacheKeys = CacheKeys<undefined, undefined, AccountAuditLogCriteria>;

type Props = {
    accountId: number,
};

export const useGetAccountAuditLog = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetAccountAuditLogCacheKeys) => apiDispatch(
        getAccountLog({
            accountId,
            accountLogsPageRequest: {
                pagination: {
                    pageSize: cacheKeys.pagination.pageSize,
                    currentPage: cacheKeys.pagination.currentPage,
                },
                actionType: cacheKeys.filters?.actionType as string | undefined,
                categories: cacheKeys.filters?.categories as Array<string>,
                startDate: cacheKeys.filters?.startDate as string | undefined,
                endDate: cacheKeys.filters?.endDate as string | undefined,
                searchQuery: cacheKeys.filters?.searchQuery?.slice(0, 200) as string | '',
            },
        }),
    ), [accountId, apiDispatch]);

    const setLoadedData = useCallback((
        response: AccountLogsPage,
        cacheKeys: GetAccountAuditLogCacheKeys,
    ) => {
        dispatch(accountAuditLogActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: accountAuditLogDataSelector,
        stateSelector: accountAuditLogStateSelector,
        resultSelector: accountAuditLogResultSelector,
        actions: accountAuditLogActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
